import { useState, useEffect } from 'react';
import { loadMonsterMapping } from '../utils/monsterMapping';


export function useUploadCounter(onUpload: (post: { id: number; title: string; image?: string; comment: string }) => void | Promise<void>) {
  // 모든 상태를 훅에서 관리
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [monsterResults, setMonsterResults] = useState<any[]>([null, null, null, null, null, null]);
  const [detecting, setDetecting] = useState(false);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [selectedAttr, setSelectedAttr] = useState<string>('Water');
  const [selectedStar, setSelectedStar] = useState<number>(1);
  const [monsterMapping, setMonsterMapping] = useState<any>(null);
  const [miniModalIdx, setMiniModalIdx] = useState<number | null>(null);
  const ATTRIBUTES = [
    { key: 'Water', label: '물' },
    { key: 'Fire', label: '불' },
    { key: 'Wind', label: '풍' },
    { key: 'Light', label: '빛' },
    { key: 'Dark', label: '암' },
  ];

  // monster_mapping.json fetch
  useEffect(() => {
    loadMonsterMapping()
      .then(data => setMonsterMapping(data));
  }, []);

  // 몬스터 인식 요청 함수
  const requestMonsterDetect = async (imgDataUrl: string) => {
    setDetecting(true);
    setMonsterResults([null, null, null, null, null, null]);
    try {
      const blob = dataURLtoBlob(imgDataUrl);
      const formData = new FormData();
      formData.append('image', blob, 'upload.png');
      const res = await fetch('/api/monsters/detect-monsters', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const result = await res.json();
        if (result.success && result.monsters) {
          const arr = [null, null, null, null, null, null];
          for (let i = 0; i < Math.min(6, result.monsters.length); i++) {
            arr[i] = result.monsters[i];
          }
          setMonsterResults(arr);
        } else {
          setMonsterResults([null, null, null, null, null, null]);
        }
      } else {
        setMonsterResults([null, null, null, null, null, null]);
      }
    } catch {
      setMonsterResults([null, null, null, null, null, null]);
    } finally {
      setDetecting(false);
    }
  };

  // 이미지 파일 업로드 핸들러
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        setImage(dataUrl);
        requestMonsterDetect(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 붙여넣기 핸들러
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            setImage(dataUrl);
            requestMonsterDetect(dataUrl);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  // base64 -> Blob 변환 함수
  function dataURLtoBlob(dataurl: string) {
    let url = dataurl;
    if (!dataurl.startsWith('data:image/')) {
      url = 'data:image/png;base64,' + dataurl;
    }
    const arr = url.split(','), mime = arr[0].match(/:(.*?);/)?.[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
    return new Blob([u8arr], { type: mime });
  }

  // 업로드 버튼 클릭 시
  const handleUpload = async () => {
    if (!title.trim()) return;
    if (!monsterResults || monsterResults.length !== 6) return;
    const attackTeam = monsterResults.slice(0, 3).map(m => ({ name: m.name, attribute: m.attribute }));
    const defenseTeam = monsterResults.slice(3, 6).map(m => ({ name: m.name, attribute: m.attribute }));
    try {
      const res = await fetch('/api/posts/UploadCounter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title,
          comment,
          attackTeam,
          defenseTeam
        })
      });
      if (res.ok) {
        alert('업로드 성공!');
        await onUpload({ id: Date.now(), title, comment });
        setTitle("");
        setImage(null);
        setComment("");
      } else {
        alert('업로드 실패');
      }
    } catch (e) {
      alert('서버 오류로 업로드 실패');
    }
  };

  // 교정 저장 핸들러 (몬스터 선택)
  const handleCorrect = async (idx: number, monster: any, currentMonsterResults?: any[]) => {
    // 현재 몬스터 결과 배열을 매개변수로 받거나 훅의 상태 사용
    const resultsToUse = currentMonsterResults || monsterResults;

    if (!resultsToUse || !resultsToUse[idx]) {
      setEditIdx(null);
      return;
    }
    
    // 훅의 상태도 업데이트 (매개변수로 받은 경우에만)
    if (currentMonsterResults) {
      setMonsterResults(currentMonsterResults.map((m, i) =>
        i === idx
          ? {
              ...m,
              name: monster.name,
              attribute: monster.attribute,
              displayName: monster.displayName,
              monster_image_base64: undefined
            }
          : m
      ));
    } else {
      setMonsterResults(results =>
        results?.map((m, i) =>
          i === idx
            ? {
                ...m,
                name: monster.name,
                attribute: monster.attribute,
                displayName: monster.displayName,
                monster_image_base64: undefined
              }
            : m
        ) ?? null
      );
    }
    
    setEditIdx(null);
    
    const m = resultsToUse[idx];
    if (m && m.crop_image_base64 && typeof m.crop_image_base64 === 'string') {
      try {
        const blob = dataURLtoBlob(m.crop_image_base64);
        const formData = new FormData();
        formData.append('image', blob, 'fail_crop.png');
        formData.append('name', m.name || 'unknown'); // 원본 인식 이름
        formData.append('correct_name', monster.name);
        formData.append('correct_attribute', monster.attribute);
        
        const response = await fetch('/api/monsters/report-fail', {
          method: 'POST',
          body: formData
        });
      } catch (error) {
      }
    }
  };

  return {
    step, setStep,
    title, setTitle,
    image, setImage,
    comment, setComment,
    monsterResults, setMonsterResults,
    detecting, setDetecting,
    hoverIdx, setHoverIdx,
    editIdx, setEditIdx,
    selectedAttr, setSelectedAttr,
    selectedStar, setSelectedStar,
    monsterMapping, setMonsterMapping,
    miniModalIdx, setMiniModalIdx,
    ATTRIBUTES,
    handleImageChange,
    handlePaste,
    requestMonsterDetect,
    handleUpload,
    handleCorrect
  };
} 